import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { dateLocale, monthNames } from '../../i18n/i18n';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import ReactToPrint from 'react-to-print';
import { useReactToPrint } from 'react-to-print';
import SortableTable from 'reactstrap-sortable-table';
import OverlayLoader from '../../components/overlayLoader';
import Skeleton, { SkeletonRows } from '../../components/skeleton';
import Pages from '../../components/pages';
import SalesInvoicePrint from '../../components/print/salesInvoice';
import { getPaginatedSales, getSaleInvoice, getSalesSummary } from '../../services/sales';
import { getTotals, getSales } from '../../services/overview';
import { getCashFlow } from '../../services/cashflow';
import { debounce, initColumns, rsdFormat, toLocalDateString } from '../../utils/helper';
import { useSheetExport } from '../../utils/queryHooks/sheetExport';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import SalesFilter from './salesFilter';
import { emailInvoice } from '../../services/email';
import { toast } from '../../utils/toast';

/* Calendar-accurate range for a period key — used to filter the sales
   transactions table so it matches the selected period ("This week" means
   Monday → Sunday of the current week, same definition used on Overview and
   Jobs, not the trend chart's rolling lookback window computed separately below). */
const periodDateRange = (period) => {
    const today = new Date();
    if (period === 'week') {
        const start = new Date(today);
        const day = start.getDay();                    // 0=Sun … 6=Sat
        start.setDate(start.getDate() + (day === 0 ? -6 : 1 - day));
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }
    if (period === 'month') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        return { start, end };
    }
    if (period === 'quarter') {
        const qStartMonth = Math.floor(today.getMonth() / 3) * 3;
        const start = new Date(today.getFullYear(), qStartMonth, 1, 0, 0, 0, 0);
        const end = new Date(today.getFullYear(), qStartMonth + 3, 0, 23, 59, 59, 999);
        return { start, end };
    }
    // year
    const start = new Date(today.getFullYear(), 0, 1, 0, 0, 0, 0);
    const end = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { start, end };
};

/* First → last day of the given month (for the "Month" period tab, where the
   user picks any month rather than always "this" one). */
const monthRange = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
};

/* Shared look for every button in the period-selector pill row (This week /
   This month / Quarter / Year / Month), so the custom-month tab matches the
   others exactly. */
const periodPillStyle = (active) => ({
    padding: '7px 15px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 700,
    border: 'none', cursor: 'pointer', transition: 'background .14s, color .14s', whiteSpace: 'nowrap',
    background: active ? 'var(--brand-grad, #3B74F2)' : 'transparent',
    color: active ? '#fff' : 'var(--muted)',
    boxShadow: active ? 'var(--sh-brand, 0 2px 8px rgba(59,116,242,.35))' : 'none',
});

/* ISO week number → the Monday of that week as a Date */
const isoWeekToDate = (ww, yy) => {
    const y = 2000 + yy;
    const d = new Date(y, 0, 4);
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - (day - 1) + (ww - 1) * 7);
    return d;
};

/* format a label for display: weekly "WW YY" → "6 Jun", monthly "MM YY" → "Jun"
   (month names in the active language, resolved on every call) */
const fmtLabel = (label, isMonthly) => {
    const parts = String(label).trim().split(/\s+/);
    if (parts.length < 2) return label;
    const n = parseInt(parts[0], 10);
    const yy = parseInt(parts[1], 10);
    if (isNaN(n) || isNaN(yy)) return label;
    const monthsShort = monthNames('short');
    if (isMonthly) return monthsShort[(n - 1 + 12) % 12];
    const d = isoWeekToDate(n, yy);
    return `${d.getDate()} ${monthsShort[d.getMonth()]}`;
};

/* Minimal SVG area chart — same visual language as the mockup. Clicking a
   point or x-axis label drills the card into that single period (value +
   delta vs the previous point), same interaction as the Overview revenue
   chart (components/charts/revenueAreaChart.jsx). Unlike that chart, this one
   doesn't have data loaded for the period before the visible window, so the
   very first point has no "vs previous" delta to show. */
const TrendChart = ({ labels, values, isMonthly }) => {
    const { t } = useTranslation();
    const gid = useId().replace(/:/g, '');
    const [selectedIndex, setSelectedIndex] = useState(null);
    if (!labels || !values || values.length < 2) {
        return <div style={{ height:226, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--muted)', fontSize:13 }}>{t('common.noData')}</div>;
    }
    const H = 210, PAD_TOP = 16, PAD_BOT = 10;
    const PLOT = H - PAD_TOP - PAD_BOT;
    const maxVal = Math.max(...values, 1);
    const y = (v) => PAD_TOP + (1 - v / maxVal) * PLOT;
    const x = (i) => (values.length === 1 ? 360 : (i / (values.length - 1)) * 720);
    const linePath = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    const areaPath = `${linePath} L${x(values.length-1).toFixed(1)},${H} L0,${H} Z`;
    const bestI = values.indexOf(Math.max(...values));
    const total = values.reduce((a, b) => a + b, 0);
    const bestLabel = fmtLabel(labels[bestI], isMonthly);

    /* pick ~6 evenly-spaced x-axis ticks */
    const step = Math.max(1, Math.floor((labels.length - 1) / 5));
    const ticks = labels.reduce((acc, l, i) => { if (i % step === 0 || i === labels.length - 1) acc.push({ i, l }); return acc; }, []);

    const sel = (selectedIndex != null && selectedIndex >= 0 && selectedIndex < values.length) ? selectedIndex : null;
    const toggleIndex = (i) => setSelectedIndex((p) => (p === i ? null : i));
    const selLabel = sel != null ? fmtLabel(labels[sel], isMonthly) : null;
    const selDelta = (sel != null && sel > 0 && values[sel - 1] > 0) ? ((values[sel] - values[sel - 1]) / values[sel - 1]) * 100 : null;

    return (
        <>
            <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:8, gap:14, flexWrap:'wrap' }}>
                {sel != null ? (
                    <>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                <span style={{ fontSize:12, fontWeight:600, color:'var(--muted)' }}>{t('sales.selectedPeriod')} · {selLabel}</span>
                                <button type="button" onClick={() => setSelectedIndex(null)} style={{
                                    fontSize:11, fontWeight:700, color:'var(--brand)', background:'var(--brand-50)',
                                    border:'1px solid var(--brand-100)', borderRadius:999, padding:'3px 9px', cursor:'pointer'
                                }}>{t('sales.viewAllBtn')}</button>
                            </div>
                            <div style={{ fontSize:26, fontWeight:800, letterSpacing:'-1px', color:'var(--ink)', fontVariantNumeric:'tabular-nums' }}>{rsdFormat(values[sel])}</div>
                        </div>
                        <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:12, fontWeight:600, color:'var(--muted)' }}>{sel > 0 ? t('sales.vsPeriod', { period: fmtLabel(labels[sel - 1], isMonthly) }) : t('sales.noPriorPeriod')}</div>
                            {selDelta != null
                                ? <div style={{ fontSize:13.5, fontWeight:800, color: selDelta < 0 ? 'var(--bad)' : 'var(--good)' }}>{selDelta < 0 ? '▼' : '▲'} {Math.abs(selDelta).toFixed(1).replace('.', ',')}%</div>
                                : <div style={{ fontSize:13.5, fontWeight:800, color:'var(--muted)' }}>—</div>}
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <div style={{ fontSize:12, fontWeight:600, color:'var(--muted)' }}>{t('sales.totalBilled')} · {fmtLabel(labels[0], isMonthly)} – {fmtLabel(labels[labels.length-1], isMonthly)}</div>
                            <div style={{ fontSize:26, fontWeight:800, letterSpacing:'-1px', color:'var(--ink)', fontVariantNumeric:'tabular-nums' }}>{rsdFormat(total)}</div>
                        </div>
                        <div style={{ textAlign:'right' }}>
                            <div style={{ fontSize:12, fontWeight:600, color:'var(--muted)' }}>{t('sales.bestPeriod')} · {bestLabel}</div>
                            <div style={{ fontSize:13.5, fontWeight:800, color:'var(--good)' }}>{rsdFormat(values[bestI])}</div>
                        </div>
                    </>
                )}
            </div>
            <div style={{ position:'relative', width:'100%', height:H, marginTop:8 }}>
                <svg style={{ width:'100%', height:'100%', display:'block', overflow:'visible' }} viewBox={`0 0 720 ${H}`} preserveAspectRatio="none">
                    <defs>
                        <linearGradient id={`rev${gid}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#3B74F2" stopOpacity="0.26"/>
                            <stop offset="100%" stopColor="#3B74F2" stopOpacity="0"/>
                        </linearGradient>
                    </defs>
                    <line x1="0" y1={y(maxVal*0.75)} x2="720" y2={y(maxVal*0.75)} stroke="#F1F3F8" strokeWidth="1"/>
                    <line x1="0" y1={y(maxVal*0.5)} x2="720" y2={y(maxVal*0.5)} stroke="#F1F3F8" strokeWidth="1"/>
                    <line x1="0" y1={y(maxVal*0.25)} x2="720" y2={y(maxVal*0.25)} stroke="#F1F3F8" strokeWidth="1"/>
                    <path d={areaPath} fill={`url(#rev${gid})`}/>
                    <path d={linePath} fill="none" stroke="#1D4FD8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {/* dots as HTML (not SVG) so they stay perfectly round under the stretched
                    viewBox, same technique as the Overview revenue chart — and each one
                    is individually clickable/focusable to drill into that period. */}
                {values.map((v, i) => {
                    const isBest = i === bestI && sel == null;
                    const size = sel === i ? 12 : isBest ? 10 : 8;
                    return (
                        <div key={i}
                            role="button" tabIndex={0}
                            onClick={() => toggleIndex(i)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleIndex(i); } }}
                            title={`${fmtLabel(labels[i], isMonthly)}: ${rsdFormat(v)}`}
                            style={{
                                position:'absolute', width:size, height:size,
                                left:`${(x(i)/720)*100}%`, top:y(v), transform:'translate(-50%,-50%)',
                                borderRadius:'50%', background:'#fff', cursor:'pointer',
                                border:`2px solid ${sel === i || isBest ? '#1D4FD8' : '#94A3B8'}`,
                                boxShadow: sel === i ? '0 0 0 4px rgba(29,79,216,.18)' : 'none',
                            }}
                        />
                    );
                })}
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, padding:'0 2px' }}>
                {ticks.map(({ i, l }) => (
                    <span key={i}
                        role="button" tabIndex={0}
                        onClick={() => toggleIndex(i)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleIndex(i); } }}
                        style={{ fontSize:11, fontWeight:700, color: sel === i ? 'var(--brand)' : 'var(--faint, #94A3B8)', cursor:'pointer' }}
                    >{fmtLabel(l, isMonthly)}</span>
                ))}
            </div>
        </>
    );
};

const SearchIcon = () => (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

/* Service names can be long (several services joined together). Truncate them
   with an ellipsis and let the user tap/click to reveal the full text — needed
   on mobile where the column is narrow. Desktop also gets a native hover tip. */
const ServiceName = ({ value }) => {
    const [open, setOpen] = useState(false);
    if (!value) return <span style={{ color: 'var(--muted)' }}>—</span>;
    return (
        <span
            className={`sb-svc${open ? ' open' : ''}`}
            style={{ fontWeight: 600, color: 'var(--text)' }}
            title={value}
            role="button"
            tabIndex={0}
            onClick={() => setOpen((o) => !o)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        >
            {value}
        </span>
    );
};

/* flatten getSales() → sorted array of {name, total, pct} */
const flattenServices = (raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const rows = [];
    for (const cat of Object.values(raw)) {
        if (cat && typeof cat === 'object') {
            for (const [svcName, val] of Object.entries(cat)) {
                rows.push({ name: svcName, total: Number(val) || 0 });
            }
        }
    }
    rows.sort((a, b) => b.total - a.total);
    const grandTotal = rows.reduce((s, r) => s + r.total, 0);
    return rows.slice(0, 4).map(r => ({ ...r, pct: grandTotal > 0 ? (r.total / grandTotal) * 100 : 0 }));
};

const RANK_COLORS = [
    'linear-gradient(90deg,#8B5CF6,#7C3AED)',
    'linear-gradient(90deg,#3B74F2,#1D4FD8)',
    'linear-gradient(90deg,#F59E0B,#D97706)',
    'linear-gradient(90deg,#34D399,#059669)',
];

const SalesPage = () => {

    const { t } = useTranslation();
    const navigate = useNavigate()

    const [sales, setSales] = useState([])
    const [pageSize, setPageSize] = useState(10)
    const [pagination, setPagination] = useState()
    const [pageNumber, setPageNumber] = useState(1)
    const [jobIdToEmail, setJobIdToEmail] = useState()
    const [invoice, setInvoice] = useState()
    const [isPrinting, setIsPrinting] = useState(false)
    const [filters, setFilters] = useState(() => {
        const { start, end } = periodDateRange('month')
        return [start, end, '', '']
    })
    const [searchText, setSearchText] = useState('')
    const [summaryData, setSummaryData] = useState()
    const [totalsData, setTotalsData] = useState()
    const [topServices, setTopServices] = useState([])
    const [period, setPeriod] = useState('month')
    const [trendSeries, setTrendSeries] = useState(null)
    // Which month is picked for the "Month" tab — null until the user actually
    // chooses one, independent of `period` so the tab remembers its last
    // selection even while another period tab is active.
    const [customMonthDate, setCustomMonthDate] = useState(null)

    const invoiceRef = useRef()
    const printRef = useRef()
    const printResolveRef = useRef()

    /* compute trend chart date range — memoized so Date objects don't change on every render
       (changing Date objects = new query key = infinite refetch loop) */
    const trendRange = useMemo(() => {
        if (period === 'customMonth' && customMonthDate) {
            const { start, end } = monthRange(customMonthDate);
            return { start, end, isMonthly: false }; // weekly breakdown within the one picked month
        }
        const end = new Date(); end.setHours(23, 59, 59, 0);
        const start = new Date(end);
        if (period === 'week')       { start.setDate(start.getDate() - 13); start.setHours(0, 0, 0, 0); }  // 2 weeks
        else if (period === 'month') { start.setDate(start.getDate() - 27); start.setHours(0, 0, 0, 0); }  // 4 weeks
        else if (period === 'quarter') { start.setDate(start.getDate() - 83); start.setHours(0, 0, 0, 0); } // 12 weeks
        else { start.setMonth(start.getMonth() - 11); start.setDate(1); start.setHours(0, 0, 0, 0); }       // 12 months
        return { start, end, isMonthly: period === 'year' };
    }, [period, customMonthDate]);

    // Picking a month from the "Month" tab's calendar: scope the trend chart +
    // Revenue tile to that exact month, and the transactions table too (same
    // as clicking any other period tab).
    const handleMonthPick = (date) => {
        if (!date) return;
        setCustomMonthDate(date);
        setPeriod('customMonth');
        setTrendSeries(null);
        const { start, end } = monthRange(date);
        setFilters((prev) => [start, end, prev[2] ?? '', prev[3] ?? '']);
        setPageNumber(1);
    };

    // Export headers stay in English: useSheetExport only formats the date
    // column when the header list contains a literal 'Date'.
    const { exportSheet: exportExcel, isExporting } = useSheetExport('Sales', sales, ['Job #', 'Date', 'Service', 'Client', 'Tax', 'Amount'])
    const { exportSheet: exportCSV, isExportingCSV } = useSheetExport('Sales', sales, ['Job #', 'Date', 'Service', 'Client', 'Tax', 'Amount'], 'csv')

    // Table header labels — initColumns() derives English ones from the field names.
    const columnLabels = {
        jobNo: t('sales.jobNo'),
        invoiceNumber: t('sales.invoiceNumber'),
        date: t('sales.date'),
        service: t('sales.service'),
        client: t('sales.client'),
        tax: t('sales.tax'),
        amount: t('sales.amount'),
    }

    const { data, isFetching } = useQuery(
        ['sales', 'paginated', pageNumber, pageSize, ...filters, searchText],
        () => getPaginatedSales(pageNumber, pageSize, ...filters, searchText)
    )
    const { data: dbSummary, isFetching: isFetchingSummary } = useQuery(['sales', 'summary'], getSalesSummary)
    const { data: dbTotals } = useQuery(['overview-totals'], getTotals, { staleTime: 1000 * 60 })
    const { data: dbSalesCategories } = useQuery(['overview-sales'], getSales, { staleTime: 1000 * 60 })
    const { data: dbTrend } = useQuery(
        ['sales-trend', period, trendRange.start, trendRange.end],
        () => getCashFlow(trendRange.isMonthly ? 'monthly' : 'weekly', trendRange.start, trendRange.end),
        { staleTime: 1000 * 60, keepPreviousData: true }
    )
    const { mutate: email, isLoading: isEmailing } = useMutation(emailInvoice, {
        onSuccess: onMutateSuccess((message) => {
            toast.success(message, { position: toast.POSITION.BOTTOM_RIGHT })
        })
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPagination(result)
            setSales(result.data.map(x => ({ ...x, tax: parseFloat(x.tax), amount: parseFloat(x.amount) })))
        })
    }, [data])

    useEffect(() => { onQuerySuccess(dbSummary, setSummaryData) }, [dbSummary])
    useEffect(() => { onQuerySuccess(dbTotals, setTotalsData) }, [dbTotals])
    useEffect(() => { onQuerySuccess(dbSalesCategories, (r) => setTopServices(flattenServices(r))) }, [dbSalesCategories])
    useEffect(() => {
        onQuerySuccess(dbTrend, (r) => {
            const labels = r?.chart?.income?.labels ?? [];
            const values = (r?.chart?.income?.data ?? []).map(v => Number(v) || 0);
            setTrendSeries({ labels, values });
        });
    }, [dbTrend])

    useEffect(() => {
        if (isPrinting && printResolveRef.current) printResolveRef.current()
    }, [isPrinting])

    const handleSetPageSize = (size) => { setPageNumber(1); setPageSize(size) }
    const handleSearchText = debounce((e) => setSearchText(e.target.value))

    const printInvoice = useReactToPrint({
        content: () => invoiceRef.current,
        onBeforeGetContent: () => new Promise((resolve) => { printResolveRef.current = resolve; setIsPrinting(true) }),
        onAfterPrint: () => { printResolveRef.current = null; setIsPrinting(false) },
    })

    const handlePrint = async (sale) => {
        const _sales = [...sales]
        const idx = _sales.findIndex(x => x.jobNo === sale.jobNo)
        _sales[idx].isFetchingInvoice = true; setSales(_sales)
        const { message, code } = await getSaleInvoice(sale.jobNo)
        _sales[idx].isFetchingInvoice = false; setSales([..._sales])
        if (code === 200) { setInvoice(message); printInvoice() }
        else toast.error(t('sales.errorGettingInvoice'), { position: toast.POSITION.TOP_RIGHT })
    }

    const handleEmail = (sale) => {
        setJobIdToEmail(sale.jobNo)
        email({ email: sale.email, jobId: sale.jobNo })
    }

    const currentMonth = monthNames('long')[new Date().getMonth()]
    const currentYear  = new Date().getFullYear()

    /* payment stats */
    const paidAmt   = Number(totalsData?.paidAmount   ?? 0)
    const unpaidAmt = Number(totalsData?.unpaidAmount ?? 0)
    const totalAmt  = paidAmt + unpaidAmt
    const paidPct   = totalAmt > 0 ? Math.round((paidAmt / totalAmt) * 100) : 0
    const unpaidPct = 100 - paidPct

    /* Revenue tile: for a picked month there's no matching bucket in
       get_sales_summary (it only knows thisWeek/thisMonth/thisYear), so the
       total comes from the trend chart's own series for that same month —
       it's already being fetched at weekly granularity for the chart below. */
    const isCustomMonth = period === 'customMonth' && !!customMonthDate
    const revenueLoading = isCustomMonth ? !trendSeries : isFetchingSummary
    const revenueValue = isCustomMonth
        ? (trendSeries?.values?.reduce((a, b) => a + b, 0) ?? 0)
        : period === 'week' ? (summaryData?.thisWeek ?? 0)
        : period === 'month' ? (summaryData?.thisMonth ?? 0)
        : (summaryData?.thisYear ?? 0)
    const revenueSubtitle = isCustomMonth
        ? customMonthDate.toLocaleDateString(dateLocale(), { month: 'long', year: 'numeric' })
        : period === 'week' ? t('sales.thisWeek')
        : period === 'month' ? t('sales.thisMonth')
        : period === 'quarter' ? t('sales.thisQuarter')
        : t('sales.yearN', { year: currentYear })

    return (
        <div className="sbr">

            {/* ── Desktop page header ── */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('sales.title')}</div>
                    <div className="sub">{t('sales.revenuePerformance', { month: currentMonth, year: currentYear })}</div>
                </div>
                <div className="act">
                    <Dropdown>
                        <Dropdown.Toggle as="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" id="sales-export-d" disabled={isFetching}>
                            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="7 10 12 15 17 10"/>
                                <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                            {t('sales.exportLabel')}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item as="button"><ReactToPrint trigger={() => <span>{t('sales.print')}</span>} content={() => printRef.current} /></Dropdown.Item>
                            <Dropdown.Item as="button" disabled={isExportingCSV} onClick={() => exportCSV()}>{isExportingCSV ? t('sales.exporting') : t('sales.csv')}</Dropdown.Item>
                            <Dropdown.Item as="button" disabled={isExporting} onClick={() => exportExcel()}>{isExporting ? t('sales.exporting') : t('sales.excel')}</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr">
                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 20V10M12 20V4M6 20v-6"/>
                        </svg>
                        {t('sales.report')}
                    </button>
                </div>
            </div>

            {/* ── Mobile appbar ── */}
            <div className="sbr-m" style={{ marginBottom:'16px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div style={{ fontSize:'22px', fontWeight:800, letterSpacing:'-0.8px', color:'var(--ink)' }}>{t('sales.title')}</div>
                    <div style={{ display:'flex', gap:'8px' }}>
                        <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" style={{ padding:'8px' }}>
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 20V10M12 20V4M6 20v-6"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Period selector ── */}
            <div className="sales-period-pills" style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#fff', border:'1px solid var(--line)', borderRadius:'999px', padding:4, boxShadow:'var(--sh-1)', marginBottom:18 }}>
                {[
                    { key:'week', label: t('sales.thisWeek') },
                    { key:'month', label: t('sales.thisMonth') },
                    { key:'quarter', label: t('sales.thisQuarter') },
                    { key:'year', label: t('sales.yearN', { year: currentYear }) },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => {
                            setPeriod(key);
                            setTrendSeries(null);
                            const { start, end } = periodDateRange(key);
                            setFilters((prev) => [start, end, prev[2] ?? '', prev[3] ?? '']);
                            setPageNumber(1);
                        }}
                        style={periodPillStyle(period === key)}
                    >{label}</button>
                ))}
                <DatePicker
                    selected={customMonthDate}
                    onChange={handleMonthPick}
                    showMonthYearPicker
                    dateFormat="MMMM yyyy"
                    portalId="sbr-dp-portal"
                    customInput={
                        <button type="button" style={periodPillStyle(period === 'customMonth')}>
                            {period === 'customMonth' && customMonthDate
                                ? customMonthDate.toLocaleDateString(dateLocale(), { month: 'short', year: 'numeric' })
                                : t('sales.selectMonth')}
                        </button>
                    }
                />
            </div>

            {/* ── KPI tiles ── */}
            <div className="kpis4" style={{ marginBottom:'18px' }}>
                {/* Revenue — lift (dark), shows selected period */}
                <div className="kpi lift">
                    <div className="top">
                        <div className="l">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 4h16v16l-3-2-3 2-2-2-2 2-3-2-3 2z"/>
                                <path d="M9 13l2-2 4 4"/>
                            </svg>
                            {t('sales.revenue')}
                        </div>
                    </div>
                    {revenueLoading
                        ? <Skeleton width="70%" height={28} style={{ margin:'10px 0 4px' }} />
                        : <div className="n">{rsdFormat(revenueValue)}</div>
                    }
                    <div className="d" style={{ fontSize:'11px', color:'#A9B6CE' }}>{revenueSubtitle}</div>
                </div>

                {/* Completed jobs */}
                <div className="kpi">
                    <div className="top">
                        <div className="l">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            {t('sales.completedJobs')}
                        </div>
                    </div>
                    {!totalsData
                        ? <Skeleton width="50%" height={28} style={{ margin:'10px 0 4px' }} />
                        : <div className="n">{totalsData?.completed ?? 0}</div>
                    }
                    <div className="d">{t('sales.allTime')}</div>
                </div>

                {/* This week revenue */}
                <div className="kpi">
                    <div className="top">
                        <div className="l">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2"/>
                                <line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/>
                                <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            {t('sales.thisWeek')}
                        </div>
                    </div>
                    {isFetchingSummary
                        ? <Skeleton width="60%" height={28} style={{ margin:'10px 0 4px' }} />
                        : <div className="n">{rsdFormat(summaryData?.thisWeek ?? 0)}</div>
                    }
                    <div className="d">{t('sales.revenue')}</div>
                </div>

                {/* Paid vs unpaid */}
                <div className="kpi">
                    <div className="top">
                        <div className="l">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="1" x2="12" y2="23"/>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                            {t('sales.paidVsUnpaid')}
                        </div>
                        {totalsData && (
                            <span style={{ fontSize:'11px', fontWeight:800, padding:'3px 9px', borderRadius:'999px', background:'var(--good-bg)', color:'var(--good)', letterSpacing:'.1px' }}>
                                {t('sales.paidPct', { pct: paidPct })}
                            </span>
                        )}
                    </div>
                    {!totalsData
                        ? <Skeleton width="60%" height={28} style={{ margin:'10px 0 4px' }} />
                        : <div className="n">{paidPct} / {unpaidPct}</div>
                    }
                    <div className="d" style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {t('sales.paidDue', { paid: rsdFormat(paidAmt), due: rsdFormat(unpaidAmt) })}
                    </div>
                </div>
            </div>

            {/* ── Revenue trend chart ── */}
            <div className="card-sbr" style={{ marginBottom:'18px' }}>
                <div className="ch">
                    <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 3v18h18"/><path d="M19 9l-5 5-4-4-3 3"/></svg>
                    <span className="t">{t('sales.revenueTrend')} · {isCustomMonth ? revenueSubtitle : period === 'week' ? t('sales.last2Weeks') : period === 'month' ? t('sales.last4Weeks') : period === 'quarter' ? t('sales.last12Weeks') : t('sales.last12Months')}</span>
                    <span className="more" style={{ marginLeft:'auto' }}>{trendRange.isMonthly ? t('sales.monthly') : t('sales.weekly')} ›</span>
                </div>
                {!trendSeries
                    ? <Skeleton width="100%" height={260} />
                    : <TrendChart labels={trendSeries.labels} values={trendSeries.values} isMonthly={trendRange.isMonthly} />
                }
            </div>

            {/* ── Two-column layout: table + rail ── */}
            <div className="cols">

                {/* ── Sales table card ── */}
                <div className="card-sbr" style={{ padding:0, overflow:'hidden', margin:0 }}>
                    <div className="ch" style={{ padding:'18px 20px 0' }}>
                        <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 8v13H3V8"/><rect x="1" y="3" width="22" height="5" rx="1"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                        <span className="t">{t('sales.salesTransactions')}</span>
                    </div>

                    {/* Filters */}
                    <div style={{ padding:'12px 20px 0' }}>
                        <SalesFilter onFilter={setFilters} />
                    </div>

                    {/* Toolbar */}
                    <div style={{ padding:'12px 20px 0', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                        <div style={{ position:'relative', flex:'1 1 200px', minWidth:0 }}>
                            <SearchIcon />
                            <input
                                className="sb-input"
                                style={{ paddingLeft:'36px', width:'100%' }}
                                type="search"
                                placeholder={t('sales.searchPlaceholder')}
                                onChange={handleSearchText}
                            />
                        </div>
                        <div style={{ display:'flex', gap:'8px', alignItems:'center', flexShrink:0 }}>
                            <select className="sb-select" onChange={(e) => handleSetPageSize(e.target.value)} defaultValue="10">
                                <option value="10">{t('sales.perPage', { n: 10 })}</option>
                                <option value="20">{t('sales.perPage', { n: 20 })}</option>
                                <option value="50">{t('sales.perPage', { n: 50 })}</option>
                                <option value="100">{t('sales.perPage', { n: 100 })}</option>
                            </select>
                            <Dropdown>
                                <Dropdown.Toggle as="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" id="sales-export" disabled={isFetching}>
                                    {t('sales.exportLabel')}
                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item as="button"><ReactToPrint trigger={() => <span>{t('sales.print')}</span>} content={() => printRef.current} /></Dropdown.Item>
                                    <Dropdown.Item as="button" disabled={isExportingCSV} onClick={() => exportCSV()}>{isExportingCSV ? t('sales.exporting') : t('sales.csv')}</Dropdown.Item>
                                    <Dropdown.Item as="button" disabled={isExporting} onClick={() => exportExcel()}>{isExporting ? t('sales.exporting') : t('sales.excel')}</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>

                    {/* Table rows */}
                    <div style={{ padding:'16px 20px 0', position:'relative', minHeight:'120px' }}>
                        {sales.length > 0 ? (
                            <div className="print-wrapper" ref={printRef}>
                                <SortableTable
                                    responsive
                                    data={sales} setData={setSales}
                                    columns={initColumns(sales, ['id', 'email', 'isFetchingInvoice', 'paymentStatus', 'paymentDueDate'])
                                        .map((col) => ({ ...col, label: columnLabels[col.value] || col.label }))}
                                    columnRender={[
                                        { column: 'jobNo',  render: (v) => (
                                            <button
                                                type="button"
                                                className="sb-job-chip sb-job-chip--link"
                                                onClick={() => navigate(`/jobs?jobId=${v}`)}
                                                title={t('sales.openThisJob')}
                                            >#{v}</button>
                                        ) },
                                        { column: 'invoiceNumber', render: (v) => v ? <span className="num" style={{ fontWeight:700, color:'var(--ink)', fontSize:'13px' }}>{v}</span> : <span style={{ color:'var(--muted)' }}>—</span> },
                                        { column: 'date',   render: (v) => <span style={{ color:'var(--muted)', fontSize:'13px', fontVariantNumeric:'tabular-nums' }}>{toLocalDateString(v)}</span> },
                                        { column: 'service',render: (v) => <ServiceName value={v} /> },
                                        { column: 'client', render: (v) => (
                                            <div className="row-sbr" style={{ border:0, padding:0, gap:'8px' }}>
                                                <span className="a">{v.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}</span>
                                                <span style={{ fontWeight:600, color:'var(--ink)' }}>{v}</span>
                                            </div>
                                        )},
                                        { column: 'tax',    render: (v) => <span className="num" style={{ color:'var(--muted)', fontSize:'13px' }}>{rsdFormat(v)}</span> },
                                        { column: 'amount', render: (v) => <span className="num" style={{ fontWeight:800, color:'var(--ink)', fontSize:'14px' }}>{rsdFormat(v)}</span> },
                                    ]}
                                    firstColumnRender={(sale) => (
                                        <div style={{ display:'flex', gap:'4px', alignItems:'center' }}>
                                            <div style={{ position:'relative', display:'inline-flex' }}>
                                                {sale.isFetchingInvoice && <OverlayLoader noTextDisplay size="25" />}
                                                <button title={t('sales.printInvoice')} onClick={() => handlePrint(sale)} className="sb-tbl-act act-primary">
                                                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="6 9 6 2 18 2 18 9"/>
                                                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                                                        <rect x="6" y="14" width="12" height="8"/>
                                                    </svg>
                                                </button>
                                            </div>
                                            {sale.email && (
                                                <div style={{ position:'relative', display:'inline-flex' }}>
                                                    {isEmailing && jobIdToEmail === sale.jobNo && <OverlayLoader noTextDisplay size="25" />}
                                                    <button title={t('sales.emailInvoice')} onClick={() => handleEmail(sale)} className="sb-tbl-act">
                                                        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                                            <polyline points="22,6 12,13 2,6"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    withTotalColumns={['tax', 'amount']}
                                    totalRender={(v) => <span className="num" style={{ fontWeight:800, fontSize:'14px', color:'var(--ink)' }}>{rsdFormat(v)}</span>}
                                    addProps={{
                                        tBodyRow: { className: 'sb-tr' },
                                        tHeading: (col) => ['tax', 'amount'].includes(col.value) ? { style: { textAlign:'right' } } : {},
                                        tData: (val, col) => ['tax', 'amount'].includes(col.value) ? { style: { textAlign:'right' } } : {},
                                    }}
                                />
                            </div>
                        ) : isFetching ? (
                            <SkeletonRows rows={6} widths={['10%', '14%', '22%', '24%', '14%', '16%']} />
                        ) : (
                            <div className="sbr-empty">
                                <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4h16v16l-3-2-3 2-2-2-2 2-3-2-3 2z"/>
                                    <path d="M9 13l2-2 4 4"/>
                                </svg>
                                {t('sales.noSalesFound')}
                            </div>
                        )}
                        {isFetching && sales.length > 0 && <OverlayLoader />}
                    </div>

                    {/* Pagination */}
                    {pagination?.totalPages > 0 && (
                        <div style={{ padding:'12px 20px 20px' }}>
                            <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                        </div>
                    )}
                </div>

                {/* ── Right rail ── */}
                <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

                    {/* Top services by revenue */}
                    <div className="card-sbr" style={{ margin:0 }}>
                        <div className="ch">
                            <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 3v18h18"/><rect x="7" y="11" width="3" height="7"/><rect x="14" y="6" width="3" height="12"/></svg>
                            <span className="t">{t('sales.topServicesByRevenue')}</span>
                        </div>
                        {topServices.length === 0 ? (
                            <div className="sbr-empty" style={{ height:100, fontSize:'13px' }}>{t('common.noData')}</div>
                        ) : (
                            <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
                                {topServices.map((svc, i) => (
                                    <div key={svc.name} style={{ display:'flex', flexDirection:'column', gap:'7px' }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:'9px' }}>
                                            <span style={{
                                                width:20, height:20, borderRadius:7,
                                                background:'var(--brand-50)', border:'1px solid var(--brand-100)',
                                                color:'var(--brand-ink)', fontSize:11, fontWeight:800,
                                                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
                                            }}>{i + 1}</span>
                                            <span style={{ fontSize:'13px', fontWeight:700, color:'var(--ink)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{svc.name}</span>
                                            <span style={{ fontSize:'13px', fontWeight:800, color:'var(--ink)', fontVariantNumeric:'tabular-nums', flexShrink:0 }}>{rsdFormat(svc.total)}</span>
                                        </div>
                                        <div className="track">
                                            <i style={{ width: `${Math.max(svc.pct, 4)}%`, background: RANK_COLORS[i] }}></i>
                                        </div>
                                        <div style={{ fontSize:'11px', fontWeight:600, color:'var(--muted)', marginTop:'-2px' }}>
                                            {t('sales.ofRevenue', { pct: svc.pct.toFixed(1).replace('.', ',') })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Payment status */}
                    <div className="card-sbr" style={{ margin:0 }}>
                        <div className="ch">
                            <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                            <span className="t">{t('sales.paymentStatus')}</span>
                        </div>
                        {!totalsData ? (
                            <Skeleton width="100%" height={60} />
                        ) : (
                            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                                {/* Progress bar */}
                                <div style={{ display:'flex', height:12, borderRadius:999, overflow:'hidden', border:'1px solid var(--line)' }}>
                                    <div style={{ width:`${paidPct}%`, background:'linear-gradient(90deg,#4ADE80,#16A34A)' }}></div>
                                    <div style={{ width:`${unpaidPct}%`, background:'linear-gradient(90deg,#FBBF24,#D97706)' }}></div>
                                </div>
                                {/* Paid row */}
                                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                    <span style={{ width:10, height:10, borderRadius:3, flexShrink:0, background:'#16A34A' }}></span>
                                    <span style={{ fontSize:'13px', fontWeight:600, color:'var(--text)' }}>{t('common.paid')}</span>
                                    <span style={{ fontSize:'11.5px', fontWeight:700, color:'var(--muted)', marginLeft:'6px' }}>{paidPct}%</span>
                                    <span style={{ marginLeft:'auto', fontSize:'13px', fontWeight:800, color:'var(--ink)', fontVariantNumeric:'tabular-nums' }}>{rsdFormat(paidAmt)}</span>
                                </div>
                                {/* Unpaid row */}
                                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                    <span style={{ width:10, height:10, borderRadius:3, flexShrink:0, background:'#D97706' }}></span>
                                    <span style={{ fontSize:'13px', fontWeight:600, color:'var(--text)' }}>{t('common.unpaid')}</span>
                                    <span style={{ fontSize:'11.5px', fontWeight:700, color:'var(--muted)', marginLeft:'6px' }}>{unpaidPct}%</span>
                                    <span style={{ marginLeft:'auto', fontSize:'13px', fontWeight:800, color:'var(--ink)', fontVariantNumeric:'tabular-nums' }}>{rsdFormat(unpaidAmt)}</span>
                                </div>
                                {/* Total */}
                                <div style={{ borderTop:'1px solid var(--line-soft)', paddingTop:'12px', display:'flex', alignItems:'center', gap:'10px' }}>
                                    <span style={{ fontSize:'13px', fontWeight:600, color:'var(--muted)' }}>{t('sales.totalBilled')}</span>
                                    <span style={{ marginLeft:'auto', fontSize:'14px', fontWeight:800, color:'var(--ink)', fontVariantNumeric:'tabular-nums' }}>{rsdFormat(totalAmt)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* This year revenue */}
                    <div className="card-sbr" style={{ margin:0 }}>
                        <div className="ch">
                            <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                            <span className="t">{t('sales.yearN', { year: currentYear })}</span>
                        </div>
                        {isFetchingSummary
                            ? <Skeleton width="70%" height={28} style={{ margin:'8px 0' }} />
                            : <div style={{ fontSize:'22px', fontWeight:800, letterSpacing:'-0.8px', color:'var(--ink)', fontVariantNumeric:'tabular-nums' }}>
                                {rsdFormat(summaryData?.thisYear ?? 0, false)} <span style={{ fontSize:'13px', fontWeight:600, color:'var(--muted)' }}>RSD</span>
                              </div>
                        }
                        <div style={{ fontSize:'12px', color:'var(--muted)', marginTop:'4px' }}>{t('sales.totalCompletedRevenue')}</div>
                    </div>
                </div>
            </div>

            {/* Hidden invoice print target */}
            <div ref={invoiceRef} className="hide-on-screen">
                <SalesInvoicePrint invoice={invoice} />
            </div>
        </div>
    );
};

export default SalesPage;
